import { MixedPaymentOption, PaymentServiceOption } from "@/queries";
import { isE2EShopper } from "@/utils";
import { useTranslation } from "i18n";
import { checkoutResponse } from "matchi-api";
import { Icon } from "ui";

// TEMPORARY. American Express stops being offered to Swedish facilities on
// 1 October 2026 (cost decision); this warns shoppers ahead of that date. A
// follow-up ticket removes it once the switch-off has settled: delete this
// file, its line in components/index.ts, the render site in PaymentForm,
// the four copy keys in locales/*/checkout.json, and the E2E-account gate
// that exists only for this notice (utils/e2eShopper.ts lists its own files).
//
// "Swedish market" is decided by the facility taking the payment, not by the
// shopper's locale: Amex is withdrawn on the facility's merchant account, so a
// Swedish-speaking shopper paying a Norwegian facility keeps Amex and an
// English-speaking one paying a Swedish facility loses it.
const AFFECTED_COUNTRY_CODE = "SE";

// Adyen's /sessions response lists, per card method, the brands the merchant
// account actually accepts for this currency (see the Apple Pay note in
// queries/paymentOptions.tsx). Once the backend withdraws Amex, "amex" drops
// out of that list and the notice hides itself, so there is no window where
// shoppers are warned about a method that is already gone.
const AMEX_BRAND = "amex";

export const offersAmex = (paymentOptions: MixedPaymentOption[]) =>
  paymentOptions.some(
    option =>
      option.type === "PAYMENT_SERVICE" &&
      (option as PaymentServiceOption).method.type === "scheme" &&
      (option as PaymentServiceOption).method.brands?.includes(AMEX_BRAND),
  );

export const AmexRemovalNotice = ({
  checkout,
  paymentOptions,
}: {
  checkout: checkoutResponse;
  paymentOptions: MixedPaymentOption[];
}) => {
  const { t } = useTranslation();

  // Temporary UI: keep it away from the mobile E2E accounts (utils/e2eShopper).
  if (
    isE2EShopper() ||
    checkout.facilityCountryCode !== AFFECTED_COUNTRY_CODE ||
    !offersAmex(paymentOptions)
  )
    return null;

  return (
    <div
      className="mb-4 space-y-2 rounded-xl bg-gray-300 p-4 text-gray-1200"
      data-testid="amex-removal-notice"
    >
      {/* A div, not a p: Icon renders a div, which is invalid inside a p. */}
      <div>
        {/* Inline so the copy wraps beneath the icon instead of beside it. */}
        <Icon
          icon="CircleInfoSolid"
          className="text-signal-warning mr-2 align-[-0.15em]"
        />
        {t(
          "American Express will no longer be available as a payment method in MATCHi from 1st of October. Other payment methods will not be affected. Please add another payment method to avoid issues with your next booking.",
        )}
      </div>
      <p>{t("We apologize for any inconvenience this may cause.")}</p>
      <p>{t("Good luck on the court!")}</p>
      <p>{t("The MATCHi team")}</p>
    </div>
  );
};
